import React, { useState } from 'react';
import { Button, Modal, InputNumber, message, Space } from 'antd';
import { SearchOutlined, LoadingOutlined } from '@ant-design/icons';
import { searchShortDramaChannels } from '../../utils/api';

/**
 * 手动触发搜索短剧频道脚本的按钮组件
 * 
 * 使用方法:
 * <SearchShortDramaChannelsButton />
 * 
 * 或者指定参数:
 * <SearchShortDramaChannelsButton maxResultsPerQuery={50} withinDays={180} />
 */
const SearchShortDramaChannelsButton = ({ 
  maxResultsPerQuery: initialMaxResults = null, 
  withinDays: initialWithinDays = null,
  onSuccess 
}) => {
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [maxResultsPerQuery, setMaxResultsPerQuery] = useState(initialMaxResults || 50);
  const [withinDays, setWithinDays] = useState(initialWithinDays || 180);

  const handleExecute = async () => {
    setLoading(true);
    try {
      const body = {};
      if (maxResultsPerQuery !== null) {
        body.maxResultsPerQuery = maxResultsPerQuery;
      }
      if (withinDays !== null) {
        body.withinDays = withinDays;
      }

      const result = await searchShortDramaChannels(maxResultsPerQuery, withinDays);

      if (result.code === 'SCRIPT_EXECUTED') {
        const data = result.data;
        const { fetchedVideoCount, matchedVideoCount, channelCount, createdCount, updatedCount, skippedCount, duration } = data;
        message.success(
          `搜索完成！获取视频: ${fetchedVideoCount}, 匹配: ${matchedVideoCount}, 频道: ${channelCount}, 新建: ${createdCount}, 更新: ${updatedCount}, 跳过: ${skippedCount || 0}, 耗时: ${duration || 'N/A'}`
        );
        
        if (onSuccess) {
          onSuccess(data);
        }
        
        setModalVisible(false);
        setMaxResultsPerQuery(initialMaxResults || 50);
        setWithinDays(initialWithinDays || 180);
      } else {
        message.error(result.message || '搜索任务执行失败');
      }
    } catch (error) {
      console.error('执行搜索任务失败:', error);
      const errorMessage = error.message || '执行搜索任务时发生错误';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = () => {
    if (initialMaxResults !== null && initialWithinDays !== null) {
      // 如果已经指定了参数，直接执行
      handleExecute();
    } else {
      // 否则打开模态框让用户输入
      setModalVisible(true);
    }
  };

  return (
    <>
      <Button
        type="primary"
        icon={loading ? <LoadingOutlined /> : <SearchOutlined />}
        onClick={handleClick}
        loading={loading}
        disabled={loading}
      >
        {loading ? '搜索中...' : '搜索短剧频道'}
      </Button>

      <Modal
        title="搜索短剧频道"
        open={modalVisible}
        onOk={handleExecute}
        onCancel={() => {
          setModalVisible(false);
          setMaxResultsPerQuery(initialMaxResults || 50);
          setWithinDays(initialWithinDays || 180);
        }}
        okText="执行"
        cancelText="取消"
        confirmLoading={loading}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <label style={{ display: 'block', marginBottom: 8 }}>每个搜索关键词最多返回结果数:</label>
            <InputNumber
              min={1}
              max={200}
              value={maxResultsPerQuery}
              onChange={(value) => setMaxResultsPerQuery(value)}
              style={{ width: '100%' }}
              placeholder="默认: 50"
            />
            <div style={{ marginTop: 8, color: '#666', fontSize: '12px' }}>
              每个搜索关键词最多返回多少条视频结果
            </div>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 8 }}>搜索最近多少天内的视频:</label>
            <InputNumber
              min={1}
              max={365}
              value={withinDays}
              onChange={(value) => setWithinDays(value)}
              style={{ width: '100%' }}
              placeholder="默认: 180"
            />
            <div style={{ marginTop: 8, color: '#666', fontSize: '12px' }}>
              只搜索最近指定天数内发布的视频
            </div>
          </div>
        </Space>
      </Modal>
    </>
  );
};

export default SearchShortDramaChannelsButton;

